import { z } from 'zod';
import { canteenNotificationIntentSchema } from './canteen-communication-notifications.js';
const uuidSchema = z.string().uuid();
const dateTimeSchema = z.string().datetime({ offset: true });
const dateSchema = z.string().date();
const quantitySchema = z.number().int().positive();
const nonNegativeQuantitySchema = z.number().int().nonnegative();
const revisionSchema = z.number().int().positive();
export const currencyCodeSchema = z.string().regex(/^[A-Z]{3}$/);
export const canteenContractVersionSchema = z.literal('1');
/**
 * A signed, short-lived authorization projection for one physical terminal.
 * It never contains a card UID or the package lookup key in plaintext.
 */
export const offlineTerminalPackageEnvelopeSchema = z.object({
    algorithm: z.literal('ECDH-P256-SHA256-AES-256-GCM'),
    recipientKeyId: z.string().min(16).max(200),
    ephemeralPublicKey: z.string().min(80).max(4096),
    salt: z.string().min(16).max(200),
    iv: z.string().min(16).max(100),
    ciphertext: z.string().min(16).max(200),
    tag: z.string().min(16).max(100),
});
export const offlineTerminalPackagePolicySchema = z.object({
    version: revisionSchema,
    packageMaxAgeMinutes: z.number().int().min(5).max(1_440),
    expiredPackageAction: z.enum(['block', 'require_exception']),
    incompleteProfileAction: z.enum(['block', 'require_exception']),
    staleRevocationAction: z.enum(['block', 'require_exception']),
});
export const offlineTerminalPackageAuthorizationSchema = z.object({
    consumerId: uuidSchema,
    reservationId: uuidSchema,
    reservationVersion: revisionSchema,
    serviceDate: dateSchema,
    lines: z.array(z.object({
        reservationLineId: uuidSchema,
        offerItemVersionId: uuidSchema,
        /**
         * All menu versions that represent the same logical dish on this date.
         * A reservation keeps its historical version, while the operator selects
         * the current published version in the terminal.
         */
        compatibleOfferItemVersionIds: z.array(uuidSchema).optional(),
        name: z.string().min(1).max(200),
        remainingQuantity: quantitySchema,
    })),
});
/**
 * A terminal may select the currently published version of a dish while a
 * reservation still references an earlier version. Offline authorization must
 * preserve the same compatibility rule as the online terminal.
 */
export function offlineAuthorizationLineMatchesOffer(line, selectedOfferItemId) {
    return (line.offerItemVersionId === selectedOfferItemId ||
        (line.compatibleOfferItemVersionIds ?? []).includes(selectedOfferItemId));
}
export const offlineTerminalPackagePayloadSchema = z.object({
    protocolVersion: z.literal(1),
    packageId: uuidSchema,
    institutionId: uuidSchema,
    canteenId: uuidSchema,
    pointId: uuidSchema,
    terminalId: uuidSchema,
    deviceId: uuidSchema,
    issuedAt: dateTimeSchema,
    validUntil: dateTimeSchema,
    policy: offlineTerminalPackagePolicySchema,
    revocationEpoch: revisionSchema,
    /** The PWA can match this token, but cannot derive a token for a new UID. */
    cardTokens: z
        .array(z.object({
        token: z.string().regex(/^sha256:[a-f0-9]{64}$/),
        consumerId: uuidSchema,
        assignmentVersion: revisionSchema,
    }))
        .default([]),
    authorization: z.array(offlineTerminalPackageAuthorizationSchema).default([]),
    lookupKeyEnvelope: offlineTerminalPackageEnvelopeSchema,
});
export const signedOfflineTerminalPackageSchema = z.object({
    keyId: z.string().min(16).max(200),
    publicKey: z.record(z.string(), z.unknown()),
    payload: offlineTerminalPackagePayloadSchema,
    signature: z.string().min(32).max(1024),
});
export const reservationStateSchema = z.enum([
    'draft',
    'confirmed',
    'partially_issued',
    'fulfilled',
    'cancelled',
]);
export const reservationLineStateSchema = z.enum([
    'confirmed',
    'partially_issued',
    'fulfilled',
    'cancelled',
]);
export const reservationLineSchema = z.object({
    id: uuidSchema,
    reservationId: uuidSchema,
    offerVariantVersionId: uuidSchema,
    serviceWindow: z.object({
        startsAt: dateTimeSchema,
        endsAt: dateTimeSchema,
    }),
    quantity: quantitySchema,
    unitPriceMinor: z.number().int().nonnegative(),
    currency: currencyCodeSchema,
    compatibilitySnapshotId: uuidSchema,
    limitSnapshotId: uuidSchema,
    state: reservationLineStateSchema,
    version: revisionSchema,
});
export const reservationSchema = z.object({
    id: uuidSchema,
    consumerId: uuidSchema,
    canteenId: uuidSchema,
    serviceDate: dateSchema,
    state: reservationStateSchema,
    version: revisionSchema,
    policySnapshotId: uuidSchema,
    createdBy: uuidSchema,
    createdAt: dateTimeSchema,
    lines: z.array(reservationLineSchema).min(1),
});
export const reservationAdjustmentSchema = z.object({
    id: uuidSchema,
    reservationLineId: uuidSchema,
    kind: z.enum(['cancel', 'restore', 'quantity_correction']),
    quantity: quantitySchema,
    reason: z.string().trim().min(3).max(500),
    actorId: uuidSchema,
    occurredAt: dateTimeSchema,
    recordedAt: dateTimeSchema,
    causationId: uuidSchema,
});
export const cardReadEvidenceSchema = z.object({
    id: uuidSchema,
    deviceId: uuidSchema,
    readerId: uuidSchema,
    mediumDerivedId: z.string().min(16).max(200),
    assignmentVersion: revisionSchema,
    deviceSequence: revisionSchema,
    readAt: dateTimeSchema,
    signature: z.string().min(32).max(4096),
    verificationState: z.enum(['verified', 'offline_verified', 'unverified']),
});
export const issuanceStateSchema = z.enum([
    'accepted',
    'accepted_with_exception',
    'corrected',
]);
export const issuanceLineSchema = z
    .object({
    id: uuidSchema,
    issuanceId: uuidSchema,
    reservationLineId: uuidSchema,
    reportedQuantity: quantitySchema,
    acceptedQuantity: nonNegativeQuantitySchema,
    exceptionQuantity: nonNegativeQuantitySchema,
    decisionCode: z.enum([
        'issued',
        'partially_issued',
        'over_limit',
        'already_issued',
        'policy_exception',
        'authorization_exception',
    ]),
})
    .refine((line) => line.acceptedQuantity + line.exceptionQuantity === line.reportedQuantity, {
    message: 'Suma ilości zaakceptowanej i wyjątkowej musi odpowiadać ilości zgłoszonej.',
    path: ['reportedQuantity'],
});
export const issuanceSchema = z.object({
    id: uuidSchema,
    reservationId: uuidSchema,
    consumerId: uuidSchema,
    pointId: uuidSchema,
    terminalId: uuidSchema,
    operatorId: uuidSchema,
    readEvidenceId: uuidSchema,
    authorizationVersion: revisionSchema,
    occurredAt: dateTimeSchema,
    recordedAt: dateTimeSchema,
    state: issuanceStateSchema,
    version: revisionSchema,
    lines: z.array(issuanceLineSchema).min(1),
});
export const issuanceCorrectionSchema = z.object({
    id: uuidSchema,
    issuanceLineId: uuidSchema,
    kind: z.enum([
        'reverse',
        'reclassify_as_accepted',
        'reclassify_as_exception',
    ]),
    quantity: quantitySchema,
    reason: z.string().trim().min(3).max(500),
    approvedBy: uuidSchema,
    occurredAt: dateTimeSchema,
    recordedAt: dateTimeSchema,
});
export const issueMealsLineSchema = z.object({
    reservationLineId: uuidSchema,
    quantity: quantitySchema,
    expectedRemaining: nonNegativeQuantitySchema,
});
export const issueMealsCommandSchema = z
    .object({
    commandType: z.literal('IssueMeals.v1'),
    commandId: uuidSchema,
    idempotencyKey: z.string().min(16).max(200),
    contractVersion: canteenContractVersionSchema,
    payloadHash: z.string().regex(/^sha256:[a-f0-9]{64}$/),
    institutionId: uuidSchema,
    canteenId: uuidSchema,
    pointId: uuidSchema,
    terminalId: uuidSchema,
    deviceSequence: revisionSchema,
    operatorId: uuidSchema,
    authorization: z.object({
        id: uuidSchema,
        version: revisionSchema,
        mode: z.enum(['online', 'offline']),
        validUntil: dateTimeSchema,
    }),
    cardReadEvidenceId: uuidSchema,
    mediumDerivedId: z.string().min(16).max(200),
    assignmentVersion: revisionSchema,
    reservationId: uuidSchema,
    expectedReservationVersion: revisionSchema,
    lines: z.array(issueMealsLineSchema).min(1),
    occurredAt: dateTimeSchema,
    recordedLocallyAt: dateTimeSchema,
    timeZone: z.string().min(1).max(100),
    dependsOnCommandIds: z.array(uuidSchema).default([]),
})
    .superRefine((command, context) => {
    const lineIds = command.lines.map((line) => line.reservationLineId);
    if (new Set(lineIds).size !== lineIds.length) {
        context.addIssue({
            code: 'custom',
            message: 'Komenda nie może zawierać tej samej linii więcej niż raz.',
            path: ['lines'],
        });
    }
    if (new Set(command.dependsOnCommandIds).size !==
        command.dependsOnCommandIds.length) {
        context.addIssue({
            code: 'custom',
            message: 'Lista zależności nie może zawierać duplikatów.',
            path: ['dependsOnCommandIds'],
        });
    }
    if (command.dependsOnCommandIds.includes(command.commandId)) {
        context.addIssue({
            code: 'custom',
            message: 'Komenda nie może zależeć od samej siebie.',
            path: ['dependsOnCommandIds'],
        });
    }
});
export const syncCommandStatusSchema = z.enum([
    'accepted',
    'duplicate',
    'conflict',
    'rejected',
    'accepted_with_exception',
    'blocked_dependency',
    'contract_upgrade_required',
]);
export const issueMealsLineResultSchema = z
    .object({
    reservationLineId: uuidSchema,
    reportedQuantity: quantitySchema,
    acceptedQuantity: nonNegativeQuantitySchema,
    exceptionQuantity: nonNegativeQuantitySchema,
    remainingQuantity: nonNegativeQuantitySchema,
    decisionCode: z.string().min(1).max(100),
})
    .refine((line) => line.acceptedQuantity + line.exceptionQuantity === line.reportedQuantity, {
    message: 'Suma ilości zaakceptowanej i wyjątkowej musi odpowiadać ilości zgłoszonej.',
    path: ['reportedQuantity'],
});
export const syncCommandResultSchema = z
    .object({
    commandId: uuidSchema,
    receiptId: uuidSchema,
    status: syncCommandStatusSchema,
    originalStatus: z
        .enum(['accepted', 'conflict', 'rejected', 'accepted_with_exception'])
        .nullable()
        .default(null),
    resultVersion: revisionSchema,
    aggregateVersion: revisionSchema.nullable(),
    lines: z.array(issueMealsLineResultSchema).default([]),
    code: z.string().min(1).max(100),
    message: z.string().min(1).max(500),
    conflictCaseId: uuidSchema.nullable().default(null),
    completedAt: dateTimeSchema,
})
    .superRefine((result, context) => {
    if (result.status === 'conflict' && result.conflictCaseId === null) {
        context.addIssue({
            code: 'custom',
            message: 'Konflikt musi wskazywać sprawę uzgodnienia.',
            path: ['conflictCaseId'],
        });
    }
    const hasException = result.lines.some((line) => line.exceptionQuantity > 0);
    if (result.status === 'accepted_with_exception' && !hasException) {
        context.addIssue({
            code: 'custom',
            message: 'Wynik wyjątkowy musi zawierać ilość wyjątkową.',
            path: ['lines'],
        });
    }
    if (result.status === 'accepted' && hasException) {
        context.addIssue({
            code: 'custom',
            message: 'Wynik zaakceptowany nie może zawierać ilości wyjątkowej.',
            path: ['lines'],
        });
    }
    if (result.status === 'duplicate' && result.originalStatus === null) {
        context.addIssue({
            code: 'custom',
            message: 'Duplikat musi wskazywać pierwotny wynik.',
            path: ['originalStatus'],
        });
    }
});
export const syncCommandReceiptSchema = z.object({
    commandId: uuidSchema,
    payloadHash: z.string().regex(/^sha256:[a-f0-9]{64}$/),
    status: syncCommandStatusSchema,
    resultVersion: revisionSchema,
    resultBody: syncCommandResultSchema,
    firstSeenAt: dateTimeSchema,
    completedAt: dateTimeSchema,
});
export const syncCommandBatchSchema = z
    .object({
    contractVersion: canteenContractVersionSchema,
    institutionId: uuidSchema,
    terminalId: uuidSchema,
    commands: z.array(issueMealsCommandSchema).min(1),
})
    .superRefine((batch, context) => {
    const commandIds = batch.commands.map((command) => command.commandId);
    if (new Set(commandIds).size !== commandIds.length) {
        context.addIssue({
            code: 'custom',
            message: 'Paczka nie może zawierać duplikatów komend.',
            path: ['commands'],
        });
    }
    batch.commands.forEach((command, index) => {
        if (command.institutionId !== batch.institutionId) {
            context.addIssue({
                code: 'custom',
                message: 'Komenda należy do innej placówki niż paczka.',
                path: ['commands', index, 'institutionId'],
            });
        }
        if (command.terminalId !== batch.terminalId) {
            context.addIssue({
                code: 'custom',
                message: 'Komenda należy do innego terminala niż paczka.',
                path: ['commands', index, 'terminalId'],
            });
        }
    });
});
export const syncCommandBatchResultSchema = z.object({
    contractVersion: canteenContractVersionSchema,
    serverTime: dateTimeSchema,
    results: z.array(syncCommandResultSchema),
    nextChangesCursor: z.string().min(1),
});
export const reservationBatchAudienceSchema = z
    .object({
    mode: z.enum(['people', 'groups', 'all_eligible']),
    personIds: z.array(uuidSchema).default([]),
    groupIds: z.array(uuidSchema).default([]),
    includeNestedGroups: z.boolean().default(true),
})
    .superRefine((audience, context) => {
    if (audience.mode === 'people' && audience.personIds.length === 0) {
        context.addIssue({
            code: 'custom',
            message: 'Audytorium osób musi zawierać co najmniej jedną osobę.',
            path: ['personIds'],
        });
    }
    if (audience.mode === 'groups' && audience.groupIds.length === 0) {
        context.addIssue({
            code: 'custom',
            message: 'Audytorium grup musi zawierać co najmniej jedną grupę.',
            path: ['groupIds'],
        });
    }
    if (audience.mode === 'all_eligible' &&
        (audience.personIds.length > 0 || audience.groupIds.length > 0)) {
        context.addIssue({
            code: 'custom',
            message: 'Tryb wszystkich uprawnionych nie przyjmuje ręcznych identyfikatorów.',
            path: ['mode'],
        });
    }
});
export const reservationBatchSelectionSchema = z
    .object({
    startsOn: dateSchema,
    endsOn: dateSchema,
    weekdays: z.array(z.number().int().min(1).max(7)).min(1),
    offerVariantVersionIds: z.array(uuidSchema).min(1),
    quantity: quantitySchema,
    intent: z.enum(['reserve', 'cancel']),
})
    .refine((selection) => selection.startsOn <= selection.endsOn, {
    message: 'Początek zakresu nie może być późniejszy niż koniec.',
    path: ['endsOn'],
});
export const reservationBatchPreviewRequestSchema = z.object({
    contractVersion: canteenContractVersionSchema,
    institutionId: uuidSchema,
    canteenId: uuidSchema,
    requestedBy: uuidSchema,
    audience: reservationBatchAudienceSchema,
    selections: z.array(reservationBatchSelectionSchema).min(1),
    idempotencyKey: z.string().min(16).max(200),
});
export const reservationBatchItemStatusSchema = z.enum([
    'planned',
    'unchanged',
    'requires_approval',
    'conflict',
    'rejected',
]);
export const reservationBatchPreviewItemSchema = z.object({
    itemId: uuidSchema,
    consumerId: uuidSchema,
    serviceDate: dateSchema,
    offerVariantVersionId: uuidSchema,
    intent: z.enum(['reserve', 'cancel']),
    quantity: quantitySchema,
    status: reservationBatchItemStatusSchema,
    unitPriceMinor: z.number().int().nonnegative(),
    financialEffectMinor: z.number().int(),
    currency: currencyCodeSchema,
    pricing: z
        .object({
        baseUnitPriceMinor: z.number().int().nonnegative(),
        resolvedUnitPriceMinor: z.number().int().nonnegative(),
        ruleId: uuidSchema.nullable(),
        ruleCode: z.string().nullable(),
        ruleVersion: z.number().int().positive().nullable(),
        ruleScope: z.enum(['default', 'person', 'group']).nullable(),
        matchedGroupIds: z.array(uuidSchema),
        audienceSnapshotHash: z.string().regex(/^sha256:[a-f0-9]{64}$/),
        audienceObservedAt: dateTimeSchema,
    })
        .optional(),
    reasonCode: z.string().min(1).max(100).nullable(),
});
export const reservationBatchCountsSchema = z.object({
    planned: nonNegativeQuantitySchema,
    unchanged: nonNegativeQuantitySchema,
    requiresApproval: nonNegativeQuantitySchema,
    conflict: nonNegativeQuantitySchema,
    rejected: nonNegativeQuantitySchema,
});
export const reservationBatchPreviewSchema = z.object({
    previewId: uuidSchema,
    previewHash: z.string().regex(/^sha256:[a-f0-9]{64}$/),
    contractVersion: canteenContractVersionSchema,
    institutionId: uuidSchema,
    canteenId: uuidSchema,
    audienceSnapshotId: uuidSchema,
    createdAt: dateTimeSchema,
    expiresAt: dateTimeSchema,
    counts: reservationBatchCountsSchema,
    totalFinancialEffectMinor: z.number().int(),
    currency: currencyCodeSchema,
    items: z.array(reservationBatchPreviewItemSchema),
});
export const reservationBatchCommitRequestSchema = z.object({
    contractVersion: canteenContractVersionSchema,
    institutionId: uuidSchema,
    canteenId: uuidSchema,
    previewId: uuidSchema,
    previewHash: z.string().regex(/^sha256:[a-f0-9]{64}$/),
    expectedAudienceSnapshotId: uuidSchema,
    idempotencyKey: z.string().min(16).max(200),
    approvedBy: uuidSchema,
});
export const reservationBatchJobStatusSchema = z.enum([
    'queued',
    'running',
    'completed',
    'completed_with_issues',
    'failed',
]);
export const reservationBatchJobSchema = z.object({
    jobId: uuidSchema,
    previewId: uuidSchema,
    status: reservationBatchJobStatusSchema,
    checkpoint: z.string().min(1).nullable(),
    planned: nonNegativeQuantitySchema,
    succeeded: nonNegativeQuantitySchema,
    unchanged: nonNegativeQuantitySchema,
    requiresApproval: nonNegativeQuantitySchema,
    conflict: nonNegativeQuantitySchema,
    rejected: nonNegativeQuantitySchema,
    failed: nonNegativeQuantitySchema,
    createdAt: dateTimeSchema,
    updatedAt: dateTimeSchema,
});
export const reservationBatchJobItemSchema = reservationBatchPreviewItemSchema.extend({
    sequence: z.number().int().positive(),
    status: z.enum([
        'planned',
        'unchanged',
        'requires_approval',
        'conflict',
        'rejected',
        'succeeded',
        'failed',
    ]),
    reservationId: uuidSchema.nullable(),
    updatedAt: dateTimeSchema,
});
export const reservationBatchJobDetailsSchema = z.object({
    jobId: uuidSchema,
    items: z.array(reservationBatchJobItemSchema),
});
export const canteenEventMetadataSchema = z.object({
    eventId: uuidSchema,
    schemaVersion: z.literal(1),
    institutionId: uuidSchema,
    aggregateId: uuidSchema,
    aggregateVersion: revisionSchema,
    occurredAt: dateTimeSchema,
    recordedAt: dateTimeSchema,
    correlationId: uuidSchema,
    causationId: uuidSchema.nullable(),
});
export const canteenOutboxEventSchema = z.discriminatedUnion('eventType', [
    canteenEventMetadataSchema.extend({
        eventType: z.literal('canteen.reservation.confirmed.v1'),
        payload: z.object({
            reservationId: uuidSchema,
            consumerId: uuidSchema,
            totalAmountMinor: z.number().int().nonnegative(),
            currency: currencyCodeSchema,
        }),
    }),
    canteenEventMetadataSchema.extend({
        eventType: z.literal('canteen.reservation.adjusted.v1'),
        payload: z.object({
            reservationId: uuidSchema,
            adjustmentId: uuidSchema,
            financialEffectMinor: z.number().int(),
            currency: currencyCodeSchema,
        }),
    }),
    canteenEventMetadataSchema.extend({
        eventType: z.literal('canteen.meals.issued.v1'),
        payload: z.object({
            issuanceId: uuidSchema,
            reservationId: uuidSchema,
            acceptedQuantity: nonNegativeQuantitySchema,
            exceptionQuantity: nonNegativeQuantitySchema,
        }),
    }),
    canteenEventMetadataSchema.extend({
        eventType: z.literal('canteen.issuance.exception-opened.v1'),
        payload: z.object({
            issuanceId: uuidSchema,
            conflictCaseId: uuidSchema,
            exceptionQuantity: quantitySchema,
            reasonCode: z.string().min(1).max(100),
        }),
    }),
    canteenEventMetadataSchema.extend({
        eventType: z.literal('canteen.issuance.corrected.v1'),
        payload: z.object({
            issuanceId: uuidSchema,
            correctionId: uuidSchema,
            quantity: quantitySchema,
            kind: issuanceCorrectionSchema.shape.kind,
        }),
    }),
    canteenEventMetadataSchema.extend({
        eventType: z.literal('canteen.food-recall.activated.v1'),
        payload: z.object({
            foodRecallId: uuidSchema,
            stockLotId: uuidSchema,
            affectedBatchIds: z.array(uuidSchema),
            actualIssuedRecipientCount: nonNegativeQuantitySchema,
            potentialReservationCount: nonNegativeQuantitySchema,
        }),
    }),
    canteenEventMetadataSchema.extend({
        eventType: z.literal('canteen.notification.intent.v1'),
        payload: canteenNotificationIntentSchema,
    }),
]);
